import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ButtonOBSOLETED } from '../../../web.common';

// Stable machine codes of the Clubhouse admin API (errorMessage in the response body).
const errorTexts = {
  'name-invalid': 'The name must be 2 to 80 characters.',
  'slug-invalid': 'The slug must be 2 to 60 characters: lowercase letters and digits, words joined by single hyphens (e.g. nordic-padel-club).',
  'slug-taken': 'Another clubhouse already uses this slug.',
  'kind-invalid': 'Unknown clubhouse kind.',
  'subtitle-too-long': 'The subtitle can be at most 200 characters.',
  'description-too-long': 'The description can be at most 2000 characters.',
  'role-invalid': 'Unknown member role.',
  'area-required': 'Choose the area of this area clubhouse.',
  'place-required': 'A house is a real place: find its address (or paste its coordinates).',
  'place-invalid': 'The exact place needs an address and valid coordinates (or leave all three empty).',
  'area-invalid': 'Unknown area, or a region without a map position (Jylland, Sjælland, Fyn, ...): choose a city or a neighbourhood.',
  'facility-required': 'Choose the facility of this facility clubhouse.',
  'facility-invalid': 'Unknown facility, or a facility without an area.',
  'member-not-found': 'This person is not a member any more.',
  'wanna-not-found': 'The wanna does not exist.',
  'has-settled-payments': 'Refused: a charge of this wanna is settled and not refunded. Cancel the game first (cancelling refunds it), then delete.',
  'reason-required': 'A reason is required.',
  'already-cancelled': 'The wanna is already cancelled.',
  'order-not-found': 'The order does not exist.',
  'order-not-settled': 'Only a settled (completed) order can be refunded.',
  'invalid-amount': 'The refund amount must be more than 0.',
  'amount-exceeds-refundable': 'The amount is more than what is left to refund on this order.',
  'wanna-not-found': 'The game of this order no longer exists.',
  'withdraw-not-a-game': 'Only a player of a game can be taken out of it.',
  'withdraw-creator': 'This player created the game and cannot leave it. Cancel the game, or hand it over to another player first. Nothing was refunded.',
  'withdraw-game-started': 'The game has already started: the seat can no longer be freed. Refund without "Also free the seat". Nothing was refunded.',
  'withdraw-game-closed': 'The game is no longer open or confirmed: there is no seat to free. Nothing was refunded.',
  'withdraw-not-joined': 'This player is not in the game any more. Refund without "Also free the seat". Nothing was refunded.',
  'has-processing-payments': 'Refused: a payment of this wanna is still processing. Wait for it to finish, then delete.',
  'has-authorized-payments': 'Refused: a charge of this wanna is authorized and not refunded. Cancel the game first (cancelling refunds it), then delete.',
  'already-expired': 'The wanna has already expired.',
  'clubhouse-not-found': 'The clubhouse does not exist.',
  'not-found': 'Not available in this environment.',
  'production-refused': 'Refused in production.',
  'demo-password-missing': 'The demo password is not configured in this environment.',
  'demo-email-in-use': 'A real account already uses a demo address.'
};

// The backend's rules for a clubhouse's texts (Logic/Clubhouses/ClubhouseCreation.cs), checked
// before submitting so the form says what is wrong instead of a refused request. The slug is
// only checked on create: it never changes afterwards. Returns the message, or null when valid.
const slugPattern = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const clubhouseFormError = ({ name, slug, subtitle, description }, { checkSlug = false } = {}) => {
  const cleanName = (name || '').trim();
  if (cleanName.length < 2 || cleanName.length > 80) return errorTexts['name-invalid'];
  if (checkSlug) {
    const cleanSlug = (slug || '').trim().toLowerCase();
    if (cleanSlug.length < 2 || cleanSlug.length > 60 || !slugPattern.test(cleanSlug)) return errorTexts['slug-invalid'];
  }
  if ((subtitle || '').trim().length > 200) return errorTexts['subtitle-too-long'];
  if ((description || '').trim().length > 2000) return errorTexts['description-too-long'];
  return null;
};

// The API's own message when it refused the action; the fallback when it did not answer.
export const apiErrorText = (e, fallback) => {
  const code = e?.payload?.responseData?.errorMessage;
  if (!code) return fallback;
  return errorTexts[code] || `${fallback} (${code})`;
};

// Minor units (øre) to a readable amount: 23000 DKK-øre -> "230.00 DKK".
export const formatMinor = (amountMinor, currencyIsoCode) => {
  if (amountMinor === null || amountMinor === undefined) return '';
  return `${(amountMinor / 100).toFixed(2)} ${currencyIsoCode || ''}`.trim();
};

export const yesNo = (value) => (value ? 'yes' : 'no');

export const linkOrText = (to, text) => (to && text ? <Link to={to}>{text}</Link> : (text || '-'));

export const externalLink = (url, label = 'Frisbii') => (url ? <a href={url} target="_blank" rel="noopener noreferrer">{label}</a> : '-');

// Shared between the list pages: "showing 1-50 of 120" with previous / next.
export const Pager = ({ skip, take, total, onChange }) => {
  if (total === undefined || total === null) return null;
  const from = total === 0 ? 0 : skip + 1;
  const to = Math.min(skip + take, total);
  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '1rem 0' }}>
      <ButtonOBSOLETED buttonContent="Previous" disabled={skip === 0} onClick={() => onChange(Math.max(0, skip - take))} />
      <span>{from}-{to} of {total}</span>
      <ButtonOBSOLETED buttonContent="Next" disabled={skip + take >= total} onClick={() => onChange(skip + take)} />
    </div>
  );
};

// The place of a clubhouse, by kind (contract v1.5): a facility clubhouse is its facility; an area clubhouse its area;
// a house is a real place and needs its exact place (address and coordinates); circles and clubs may have an exact
// place, an area as a rough reference, both or neither. Areas and facilities are open communities; the other kinds
// are closed (the API decides it from the kind). An exact place measures "courts nearby" exactly, an area only
// approximately, from its centre.
const kindOf = (kind) => String(kind || '').toLowerCase();
export const isFacilityKind = (kind) => kindOf(kind) === 'facility';
export const isAreaKind = (kind) => kindOf(kind) === 'area';
export const needsExactPlace = (kind) => kindOf(kind) === 'house';
export const takesExactPlace = (kind) => ['house', 'circle', 'club'].includes(kindOf(kind));

export const emptyExactPlace = { addressText: '', latitude: '', longitude: '' };

const hasExactPlace = (c) => c.hasExactPlace || (c.latitude != null && c.longitude != null);

export const placeText = (c) => {
  if (c.facilitySlug) return `${c.facilitySlug} (${c.areaSlug || '-'})`;
  const parts = [hasExactPlace(c) ? (c.addressText || 'exact place') : null, c.areaSlug ? `area ${c.areaSlug}` : null].filter(Boolean);
  if (parts.length) return parts.join(' · ');
  return needsExactPlace(c.kind) || isAreaKind(c.kind) ? 'missing' : 'none (courts near each member\'s home)';
};

// True when the kind requires a place the clubhouse does not have.
export const placeMissing = (c) => {
  if (isFacilityKind(c.kind)) return !c.facilitySlug;
  if (isAreaKind(c.kind)) return !c.areaSlug;
  if (needsExactPlace(c.kind)) return !hasExactPlace(c);
  return false;
};

const numberOrNull = (value) => {
  const n = parseFloat(String(value ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

// The place fields of a create or edit request for this kind.
export const placePayload = (kind, place) => {
  if (isFacilityKind(kind)) return { areaSlug: null, facilitySlug: (place.facilitySlug || '').trim() };
  const exact = takesExactPlace(kind) && (place.addressText || '').trim();
  return {
    areaSlug: (place.areaSlug || '').trim() || null,
    facilitySlug: null,
    addressText: exact ? place.addressText.trim() : null,
    latitude: exact ? numberOrNull(place.latitude) : null,
    longitude: exact ? numberOrNull(place.longitude) : null,
  };
};

// Whether the place fields are complete enough to send (the API checks them again).
export const placeReady = (kind, place) => {
  if (isFacilityKind(kind)) return !!(place.facilitySlug || '').trim();
  if (isAreaKind(kind)) return !!(place.areaSlug || '').trim();
  const p = placePayload(kind, place);
  const exactComplete = !!p.addressText && p.latitude != null && p.longitude != null;
  const exactEmpty = !(place.addressText || '').trim() && numberOrNull(place.latitude) == null && numberOrNull(place.longitude) == null;
  return needsExactPlace(kind) ? exactComplete : exactComplete || exactEmpty;
};

// A text field for a slug, with suggestions while typing (native datalist, like the plain inputs of these pages).
// The value is the slug itself, so an existing clubhouse shows its current slug.
export const SlugLookup = ({ id, value, onChange, loadOptions, placeholder }) => {
  const [options, setOptions] = useState([]);

  useEffect(() => {
    const key = (value || '').trim();
    if (key.length < 2) {
      setOptions([]);
      return undefined;
    }
    let alive = true;
    const timer = setTimeout(() => {
      loadOptions(key)
        .then((found) => { if (alive) setOptions(found || []); })
        .catch(() => { if (alive) setOptions([]); });
    }, 250);
    return () => { alive = false; clearTimeout(timer); };
  }, [value]);

  return (
    <React.Fragment>
      <input type="text" list={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={{ display: 'block', width: '100%' }} />
      <datalist id={id}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </datalist>
    </React.Fragment>
  );
};

// Address search for the exact place: OpenStreetMap's Nominatim (no key; a handful of admin lookups is well within
// its usage policy). Picking a result fills the address and the coordinates; both stay editable, so coordinates can
// also be pasted from Google Maps ("55.6674, 12.6043").
const searchAddress = async (query) => {
  const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&countrycodes=dk&limit=5&q=${encodeURIComponent(query)}`;
  const response = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!response.ok) throw new Error(`address search failed (${response.status})`);
  return (await response.json()).map((r) => ({ label: r.display_name, latitude: parseFloat(r.lat), longitude: parseFloat(r.lon) }));
};

export const ExactPlaceField = ({ kind, place, onChange }) => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const set = (fields) => onChange({ ...place, ...fields });

  const find = async () => {
    setError(null);
    setResults(null);
    try {
      const found = await searchAddress(place.addressText || '');
      setResults(found);
      if (!found.length) setError('No address found: try street, number and postcode, or paste the coordinates.');
    } catch (e) {
      setError(String(e.message || e));
    }
  };

  const pasteCoordinates = (value) => {
    const m = String(value).match(/(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/);
    if (m) set({ latitude: m[1], longitude: m[2] });
  };

  return (
    <fieldset style={{ border: '1px solid #e5e5e5', padding: '0.75rem' }}>
      <legend>Exact place {needsExactPlace(kind) ? '(required for a house)' : '(optional)'}</legend>
      <label>Address
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <input type="text" value={place.addressText} maxLength={300} placeholder="Dalslandsgade 8, 2300 København S" onChange={(e) => set({ addressText: e.target.value })} style={{ flex: 1 }} />
          <ButtonOBSOLETED buttonContent="Find" disabled={!(place.addressText || '').trim()} onClick={find} />
        </div>
      </label>
      {results && results.length > 0 && (
        <ul style={{ margin: '0.5rem 0', paddingLeft: '1.2rem' }}>
          {results.map((r) => (
            <li key={`${r.latitude},${r.longitude}`}>
              <a href="#" onClick={(e) => { e.preventDefault(); set({ latitude: String(r.latitude), longitude: String(r.longitude) }); setResults(null); }}>{r.label}</a>
            </li>
          ))}
        </ul>
      )}
      {error && <p style={{ ...warningTextStyle, margin: '0.5rem 0' }}>{error}</p>}
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
        <label style={{ flex: 1 }}>Latitude
          <input type="text" value={place.latitude} onChange={(e) => set({ latitude: e.target.value })} onPaste={(e) => { e.preventDefault(); pasteCoordinates(e.clipboardData.getData('text')); }} placeholder="55.6674" style={{ display: 'block', width: '100%' }} />
        </label>
        <label style={{ flex: 1 }}>Longitude
          <input type="text" value={place.longitude} onChange={(e) => set({ longitude: e.target.value })} placeholder="12.6043" style={{ display: 'block', width: '100%' }} />
        </label>
      </div>
      {place.latitude && place.longitude && (
        <a href={`https://www.google.com/maps?q=${encodeURIComponent(`${place.latitude},${place.longitude}`)}`} target="_blank" rel="noopener noreferrer">Check on the map</a>
      )}
    </fieldset>
  );
};

// The place fields for the kind of clubhouse: the facility, or an area (required for an area clubhouse, a rough
// reference otherwise) and the exact place for houses, circles and clubs.
export const PlaceField = ({ api, kind, place, onChange }) => (isFacilityKind(kind) ? (
  <label>Facility (type a name, pick the slug)
    <SlugLookup id="clubhouse-facility" value={place.facilitySlug} onChange={(facilitySlug) => onChange({ ...place, facilitySlug })} loadOptions={(key) => api.clubhouseAdmin.lookupFacilitySlugs(key)} placeholder="dgi-byen-koebenhavn" />
  </label>
) : (
  <React.Fragment>
    {takesExactPlace(kind) && <ExactPlaceField kind={kind} place={place} onChange={onChange} />}
    <label>Area {isAreaKind(kind) ? '' : '(optional: a rough reference, used when there is no exact place)'} (type a name, pick the slug)
      <SlugLookup id="clubhouse-area" value={place.areaSlug} onChange={(areaSlug) => onChange({ ...place, areaSlug })} loadOptions={(key) => api.clubhouseAdmin.lookupAreaSlugs(key)} placeholder="amager" />
    </label>
  </React.Fragment>
));

export const panelStyle = { width: '80%', margin: '1rem auto', backgroundColor: 'white', border: '1px solid #e5e5e5', padding: '1.2rem' };
export const buttonRowStyle = { justifyContent: 'center', display: 'flex', gap: '1.5rem', margin: '1rem 0' };
export const warningTextStyle = { color: '#8A1C1C' };
